import { useState } from 'react'
import { useOnboardingSession } from '../hooks/useOnboardingSession'
import { ModeSelection } from '../components/onboarding/ModeSelection'
import { ComingSoonModal } from '../components/onboarding/ComingSoonModal'
import { ProgressIndicator } from '../components/onboarding/ProgressIndicator'
import { LovedOneNameStep } from '../components/onboarding/steps/LovedOneNameStep'
import { LovedOnePhoneStep } from '../components/onboarding/steps/LovedOnePhoneStep'
import { LovedOneEmailStep } from '../components/onboarding/steps/LovedOneEmailStep'
import { LanguagePreferenceStep } from '../components/onboarding/steps/LanguagePreferenceStep'
import { CommunicationConsentStep } from '../components/onboarding/steps/CommunicationConsentStep'
import { useAuth0 } from '../hooks/useAuth0'

type Step = 'mode' | 'name' | 'phone' | 'email' | 'language' | 'consent'

/**
 * Multi-step onboarding flow container (FR-002, FR-003, FR-044, FR-045)
 * Manages state and navigation between steps
 */
export function OnboardingFlow() {
  const { session, updateSession } = useOnboardingSession()
  const { loginWithRedirect } = useAuth0()
  const [currentStep, setCurrentStep] = useState<Step>('mode')
  const [showComingSoon, setShowComingSoon] = useState(false)

  const handleModeSelection = (mode: 'ai-guided' | 'form-based') => {
    if (mode === 'ai-guided') {
      setShowComingSoon(true)
      updateSession({ mode: 'ai-guided' })
    } else {
      updateSession({ mode: 'form-based' })
      setCurrentStep('name')
    }
  }

  const handleComingSoonClose = () => {
    setShowComingSoon(false)
    updateSession({ mode: 'form-based' })
    setCurrentStep('name')
  }

  const handleNameNext = (name: string) => {
    updateSession({ lovedOneName: name })
    setCurrentStep('phone')
  }

  const handlePhoneNext = (phone: string) => {
    updateSession({ lovedOnePhone: phone })
    setCurrentStep('email')
  }

  const handleEmailNext = (email: string) => {
    updateSession({ lovedOneEmail: email || undefined })
    setCurrentStep('language')
  }

  const handleEmailSkip = () => {
    updateSession({ lovedOneEmail: undefined })
    setCurrentStep('language')
  }

  const handleLanguageNext = (language: 'en' | 'zh-Hant') => {
    console.log('Language Next clicked:', language)

    updateSession({
      languagePreference: language,
    })

    // Move to consent step
    setCurrentStep('consent')
  }

  const handleConsentNext = (data: {
    smsConsent: boolean
    emailConsent: boolean
    caregiverPhone?: string
    caregiverEmail?: string
  }) => {
    updateSession({
      smsConsent: data.smsConsent,
      emailConsent: data.emailConsent,
      caregiverPhone: data.caregiverPhone,
      caregiverEmail: data.caregiverEmail,
    })

    console.log('Initiating Auth0 login redirect...')

    // Redirect to Auth0 for OAuth authentication (FR-005)
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup', // Force Auth0 to show signup screen for new users
      },
      appState: {
        returnTo: '/auth/callback',
      },
    })
  }

  const handleConsentSkip = () => {
    updateSession({
      smsConsent: false,
      emailConsent: false,
      caregiverPhone: undefined,
      caregiverEmail: undefined,
    })

    console.log('Initiating Auth0 login redirect...')

    // Redirect to Auth0 for OAuth authentication (FR-005)
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup', // Force Auth0 to show signup screen for new users
      },
      appState: {
        returnTo: '/auth/callback',
      },
    })
  }

  const containerStyles = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom, #f0f9ff, #ffffff)', // sky-50 to white
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const headerStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid #f3f4f6',
    padding: '1rem 0',
    marginBottom: '2rem',
  }

  const headerContentStyles = {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  }

  const contentWrapperStyles = {
    padding: '2rem 1rem',
  }

  const contentStyles = {
    maxWidth: '600px',
    margin: '0 auto',
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '2rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  }

  const getTotalSteps = () => {
    return 5 // name, phone, email, language, consent
  }

  const getCurrentStepNumber = () => {
    switch (currentStep) {
      case 'name':
        return 1
      case 'phone':
        return 2
      case 'email':
        return 3
      case 'language':
        return 4
      case 'consent':
        return 5
      default:
        return 0
    }
  }

  return (
    <div style={containerStyles}>
      {/* Header */}
      <div style={headerStyles}>
        <div style={headerContentStyles}>
          <img
            src="/favicon.png"
            alt="Little Sister Logo"
            style={{
              height: '40px',
              width: '40px',
              objectFit: 'contain',
            }}
          />
          <span
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#111827',
            }}
          >
            Little Sister
          </span>
        </div>
      </div>

      {currentStep === 'mode' ? (
        <div style={contentWrapperStyles}>
          <ModeSelection onSelectMode={handleModeSelection} />
        </div>
      ) : (
        <div style={contentWrapperStyles}>
          <div style={contentStyles}>
            <ProgressIndicator currentStep={getCurrentStepNumber()} totalSteps={getTotalSteps()} />

            {currentStep === 'name' && (
              <LovedOneNameStep initialValue={session.lovedOneName} onNext={handleNameNext} />
            )}

            {currentStep === 'phone' && (
              <LovedOnePhoneStep
                initialValue={session.lovedOnePhone}
                onNext={handlePhoneNext}
                onBack={() => setCurrentStep('name')}
              />
            )}

            {currentStep === 'email' && (
              <LovedOneEmailStep
                initialValue={session.lovedOneEmail}
                onNext={handleEmailNext}
                onSkip={handleEmailSkip}
                onBack={() => setCurrentStep('phone')}
              />
            )}

            {currentStep === 'language' && (
              <LanguagePreferenceStep
                initialValue={session.languagePreference}
                onNext={handleLanguageNext}
                onBack={() => setCurrentStep('email')}
              />
            )}

            {currentStep === 'consent' && (
              <CommunicationConsentStep
                initialSmsConsent={session.smsConsent}
                initialEmailConsent={session.emailConsent}
                initialCaregiverPhone={session.caregiverPhone}
                initialCaregiverEmail={session.caregiverEmail}
                onNext={handleConsentNext}
                onSkip={handleConsentSkip}
                onBack={() => setCurrentStep('language')}
              />
            )}
          </div>
        </div>
      )}

      {showComingSoon && <ComingSoonModal onClose={handleComingSoonClose} />}
    </div>
  )
}
